import React, { useState } from 'react';
import { Languages, Target, BookOpen, Folder, Settings, Plus, Activity, Coins, Lock, LogOut, KeyRound, ChevronUp, Gift, Sparkles } from 'lucide-react';
import { WorkspaceConnector } from '../WorkspaceConnector';
import { isCommercial, isInternal, features } from '../../config/appMode';
import GamificationModal from '../modals/GamificationModal';
import PrivacyModal, { PrivacyMode } from '../modals/PrivacyModal';
import { Shield, Cloud, HelpCircle, LifeBuoy } from 'lucide-react';
import { supabase } from '../../services/supabase/supabaseClient';
import { usePrivacyStore } from '../../store/usePrivacyStore';

export type SidebarMode = 'translating' | 'training' | 'terminology' | 'admin';

interface SidebarProps {
    activeMode: SidebarMode;
    onModeChange: (mode: SidebarMode) => void;
    counts: {
        translating: number;
        training: number;
    };
    onCreateProject?: () => void;
    isAdmin?: boolean;
    // Commercial footer info
    userName?: string;
    userEmail?: string;
    userRole?: string;
    credits?: number;
    onSignOut?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
    activeMode,
    onModeChange,
    counts,
    onCreateProject,
    isAdmin,
    userName,
    userEmail,
    userRole,
    credits,
    onSignOut,
}) => {
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showGamification, setShowGamification] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    // US-12-01: read from persist store instead of local state
    const privacyMode = usePrivacyStore(s => s.mode);
    const setPrivacyMode = usePrivacyStore(s => s.setMode);
    const [resetting, setResetting] = useState(false);

    // US-15-04: Wire "Đổi mật khẩu" → Supabase reset email
    const handleResetPassword = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) {
            alert('Chưa đăng nhập');
            return;
        }
        const ok = window.confirm(
            `Gửi link đổi mật khẩu tới ${user.email}?\n\nBạn sẽ nhận email trong 1-2 phút (kiểm tra cả Spam).`
        );
        if (!ok) return;
        setResetting(true);
        const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
            redirectTo: `${window.location.origin}/#/reset-password`,
        });
        setResetting(false);
        if (error) {
            alert(`Lỗi gửi email: ${error.message}`);
        } else {
            alert(`Đã gửi link đổi mật khẩu tới ${user.email}`);
            setShowUserMenu(false);
        }
    };

    const navItems = [
        {
            id: 'translating',
            label: 'Dịch tài liệu',
            icon: Languages,
            count: counts.translating,
            color: 'text-indigo-600',
            bg: 'bg-indigo-50'
        },
        // Terminology (both modes)
        {
            id: 'terminology',
            label: 'Từ điển thuật ngữ',
            icon: BookOpen,
            count: null,
            color: 'text-emerald-600',
            bg: 'bg-emerald-50'
        },
        // Training (both modes)
        {
            id: 'training',
            label: 'Huấn luyện',
            icon: Target,
            count: counts.training,
            color: 'text-amber-600',
            bg: 'bg-amber-50'
        },
        // Credit shop (commercial only)
        ...(isCommercial ? [{
            id: 'shop',
            label: 'Nạp Credits',
            icon: Coins,
            count: null,
            color: 'text-violet-600',
            bg: 'bg-violet-50'
        }] : []),
        // Admin (both modes, if admin)
        ...(isAdmin && features.adminDashboard ? [{
            id: 'admin',
            label: 'Admin',
            icon: Activity,
            count: null,
            color: 'text-violet-600',
            bg: 'bg-violet-50'
        }] : []),
    ];

    return (
        <aside className="w-64 h-full bg-slate-50 border-r border-slate-200 flex flex-col shrink-0 overflow-y-auto">
            {/* Logo & Brand */}
            <div className="p-6 flex items-center gap-3">
                <div className="relative w-11 h-11 rounded-xl bg-white shadow-lg shadow-emerald-500/15 border border-emerald-100 flex items-center justify-center shrink-0">
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-50 to-white" />
                    <img src="/icons/logo-64.png" alt="Dịch Tài Liệu" className="relative w-7 h-7" />
                </div>
                <div>
                    <h1 className="font-black text-slate-800 tracking-tight leading-none text-lg">
                        Dịch Tài Liệu
                    </h1>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 inline-block">
                        AI Translation
                    </span>
                </div>
            </div>

            <div className="flex-1 px-4 py-6">
                {/* Navigation Section */}
                <div className="space-y-8">
                    <div>
                        <h3 className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
                            Danh mục
                        </h3>
                        <div className="space-y-1">
                            {navItems.map((item) => {
                                const isActive = activeMode === item.id;
                                const Icon = item.icon;

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => onModeChange(item.id as SidebarMode)}
                                        data-tour={`nav-${item.id}`}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group ${isActive
                                            ? `${item.bg} ${item.color} shadow-sm ring-1 ring-slate-200/50`
                                            : 'text-slate-500 hover:bg-white hover:text-slate-800 hover:shadow-sm'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                                            <span className={`text-sm font-bold ${isActive ? 'text-slate-900' : ''}`}>
                                                {item.label}
                                            </span>
                                        </div>
                                        {item.count !== null && (
                                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${isActive ? `${item.bg} ${item.color} ring-1 ring-current/20` : 'bg-slate-200 text-slate-500'
                                                }`}>
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Workspace Section — commercial + internal đều show để user connect FS */}
                    <div>
                        <h3 className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
                            Lưu trữ
                        </h3>
                        <div className="px-2 space-y-2" data-tour="workspace">
                            <WorkspaceConnector />

                            {isCommercial && (
                                <button
                                    onClick={() => setShowPrivacy(true)}
                                    data-tour="privacy-mode"
                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border transition-all hover:-translate-y-0.5 ${
                                        privacyMode === 'incognito'
                                            ? 'bg-fuchsia-50/50 border-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-50 hover:border-fuchsia-200'
                                            : 'bg-indigo-50/50 border-indigo-100 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-200'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        {privacyMode === 'incognito' ? <Shield className="w-3.5 h-3.5" /> : <Cloud className="w-3.5 h-3.5" />}
                                        <span className="text-[11px] font-bold">
                                            {privacyMode === 'incognito' ? 'Dịch Ẩn Danh' : 'Dịch Ngầm'}
                                        </span>
                                    </div>
                                    <span className="text-[9px] font-black tracking-widest uppercase opacity-50">CẤU HÌNH</span>
                                </button>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* Bottom Footer */}
            {isCommercial && userName ? (
                <div className="border-t border-slate-100 bg-white/50 backdrop-blur-sm">
                    {/* Gamification Banner — tạm ẩn (chưa wire chương trình +100K) */}
                    {false && (
                    <div className="px-3 pt-3" data-tour="gift">
                        <button
                            onClick={() => setShowGamification(true)}
                            className="w-full relative overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 p-2.5 text-white shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 active:scale-95 group text-left border border-fuchsia-400/30"
                        >
                            {/* Animated gradient overlay for shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>

                            <div className="flex items-center gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 border border-white/30 backdrop-blur-sm shadow-inner">
                                    <Gift className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[11px] font-black tracking-wide flex items-center gap-1.5 shadow-sm">
                                        NHẬN QUÀ TẶNG <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                                    </div>
                                    <div className="text-[9px] text-white/90 font-medium mt-0.5 flex items-center gap-1">
                                        Xác thực SĐT nhận <span className="bg-white/20 px-1 rounded text-white font-bold">+100K</span>
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                    )}

                    {/* User Profile Block */}
                    <div className="px-3 pb-4 pt-1 relative">
                        <button
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="w-full flex items-center justify-between p-2.5 hover:bg-white rounded-2xl transition-all border border-transparent hover:border-slate-200/60 hover:shadow-sm group"
                        >
                            <div className="flex items-center gap-3">
                                {/* Premium Avatar */}
                                <div className="relative">
                                    <div className="w-10 h-10 bg-gradient-to-tr from-indigo-600 to-violet-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md ring-2 ring-white">
                                        {(userName || '?')[0].toUpperCase()}
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 bg-emerald-500 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm"></div>
                                </div>
                                
                                <div className="text-left flex-1 min-w-0">
                                    {/* Name & Role */}
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <p className="text-sm font-black text-slate-800 truncate leading-none pt-0.5">{userName}</p>
                                        {userRole === 'admin' && (
                                            <span className="px-1.5 py-0.5 bg-slate-800 text-amber-300 text-[8px] font-black rounded uppercase tracking-wider leading-none shadow-sm shrink-0">
                                                Admin
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Stats Subtitle */}
                                    <div className="flex items-center gap-2 mt-0.5" data-tour="credits">
                                        {credits !== undefined && (
                                            <span className="flex items-center gap-1 text-[11px] font-bold text-slate-600">
                                                <Coins className="w-3.5 h-3.5 text-amber-500" />
                                                {credits >= 1000 ? Math.round(credits / 1000) + 'K' : credits}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <ChevronUp className={`w-4 h-4 text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : 'group-hover:-translate-y-0.5'}`} />
                        </button>

                        {/* Dropdown menu */}
                        {showUserMenu && (
                            <div className="absolute bottom-full left-3 right-3 mb-2 bg-white rounded-2xl shadow-xl border border-slate-200 p-2 z-50 animate-in slide-in-from-bottom-2 duration-200">
                                <a
                                    href="/help"
                                    onClick={() => setShowUserMenu(false)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-colors"
                                >
                                    <LifeBuoy className="w-4 h-4" /> Trợ giúp
                                </a>
                                <a
                                    href="#/privacy"
                                    onClick={() => setShowUserMenu(false)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-colors"
                                >
                                    <Shield className="w-4 h-4" /> Chính sách bảo mật
                                </a>
                                <button
                                    onClick={() => {
                                        setShowUserMenu(false);
                                        window.dispatchEvent(new CustomEvent('tour:replay'));
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-colors"
                                >
                                    <HelpCircle className="w-4 h-4" /> Xem lại hướng dẫn
                                </button>
                                <button
                                    onClick={handleResetPassword}
                                    disabled={resetting}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-50"
                                >
                                    <KeyRound className="w-4 h-4" /> {resetting ? 'Đang gửi...' : 'Đổi mật khẩu'}
                                </button>
                                <div className="h-px bg-slate-100 my-1 mx-2"></div>
                                {onSignOut && (
                                    <button
                                        onClick={onSignOut}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                    >
                                        <LogOut className="w-4 h-4" /> Đăng xuất
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="p-4 border-t border-slate-100 bg-white/50 backdrop-blur-sm">
                    <button className="w-full flex items-center gap-3 px-3 py-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors group">
                        <Settings className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                        <span className="text-xs font-bold">Settings</span>
                    </button>
                </div>
            )}

            {/* Support email — luôn hiện cuối sidebar */}
            <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400 leading-relaxed">
                Cần hỗ trợ?{' '}
                <a
                    href="mailto:support@dichtailieu.vn"
                    className="text-emerald-600 hover:text-emerald-700 font-semibold"
                >
                    support@dichtailieu.vn
                </a>
            </div>

            {/* Modals */}
            <GamificationModal 
                isOpen={showGamification} 
                onClose={() => setShowGamification(false)} 
                currentTokens={credits || 0}
            />

            <PrivacyModal 
                isOpen={showPrivacy}
                onClose={() => setShowPrivacy(false)}
                currentMode={privacyMode}
                onModeChange={setPrivacyMode}
            />
        </aside>
    );
};

export default Sidebar;
